import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stethoscope, Plus, Trash2, AlertCircle } from 'lucide-react';
import { publicAPI, vetAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from "@/contexts/AuthContext";
import { handleSubmit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import LocationEditor from "@/components/LocationEditor";
import ImageUpload from "@/components/ImageUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import WaiverCheckBox from "@/components/WaiverCheckBox";
import Section from "@/components/Section";
import { computeCosts, defaultPricing } from "@/utils/costUtil";

const PREDEFINED_SERVICES = [
	'vaccination', 'deworming', 'dental', 'farriery', 'xray', 'ultrasound',
	'emergency_call', 'pre_purchase_exam', 'gelding', 'colic_surgery',
	'lameness_eval', 'sedation', 'microchipping', 'coggins_test',
	'endoscopy', 'reproduction_check',
];
const SPECIALTIES = [
	'equine_dentistry', 'reproduction', 'surgery', 'sports_medicine',
	'emergency', 'podiatry', 'chiropractic', 'acupuncture',
	'rehabilitation', 'general_practice',
];
const UNITS = ['visit', 'hour', 'procedure'];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CreateVetAdPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();
	const { user } = useAuth();
	const [images, setImages] = useState([]);
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState({
		title: '',
		description: '',
		price: '',
		predefined_services: [],
		service_prices: {},
		custom_services: [],
		specialties: [],
		emergency_24_7: false,
		mobile_only: false,
		travels_to_client: false,
		license_number: '',
		experience_years: '',
		clinic_name: '',
		website: '',
		contact_phone: '',
		location_name: '',
		location_lat: null,
		location_lng: null,
		is_featured: false,
		featured_paid: false,
		hidden_images: [],
		hidden_videos: []
	});

	useEffect(() => {
		publicAPI.settings()
			.then(r => setPricing(
				prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		if (editId) {
			vetAPI.get(editId).then(r => {
				const ad = r.data;
				setForm({
					title: ad.title || '',
					description: ad.description || '',
					price: ad.price || '',
					predefined_services: ad.predefined_services || [],
					service_prices: ad.service_prices || {},
					custom_services: ad.custom_services || [],
					specialties: ad.specialties || [],
					emergency_24_7: ad.emergency_24_7 || false,
					mobile_only: ad.mobile_only || false,
					travels_to_client: ad.travels_to_client || false,
					license_number: ad.license_number || '',
					experience_years: ad.experience_years || '',
					clinic_name: ad.clinic_name || '',
					website: ad.website || '',
					contact_phone: ad.contact_phone || '',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
				});

				// Load existing images as thumbnails
				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);
			}).catch((e) => {
				console.log(e)
				navigate('/dashboard')
			});
		}
	}, [user, editId, navigate]);

	const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

	const togglePredefinedService = (key) => {
		setForm((p) => {
			const has = p.predefined_services.includes(key);
			const services = has ? p.predefined_services.filter(x => x !== key) : [...p.predefined_services, key];
			const prices = { ...p.service_prices };
			if (has) delete prices[key];
			return { ...p, predefined_services: services, service_prices: prices };
		});
	};

	const setServicePrice = (key, value) => {
		setForm((p) =>
			({ ...p, service_prices: { ...p.service_prices, [key]: value === '' ? '' : parseFloat(value) } }));
	};

	const toggleSpecialty = (key) => {
		setForm((p) =>
			({ ...p, specialties: p.specialties.includes(key) ?
					p.specialties.filter(x => x !== key) : [...p.specialties, key] }));
	};

	const addCustomService = () => {
		setForm((p) =>
			({ ...p, custom_services: [...p.custom_services, { key: 'custom', name: '', price: '', unit: 'visit' }] }));
	};

	const updateCustomService = (i, field, value) => {
		setForm((p) => ({
			...p,
			custom_services: p.custom_services.map((s, idx) =>
				idx === i ? { ...s, [field]: field === 'price' ? (value === '' ? '' : parseFloat(value)) : value } : s),
		}));
	};

	const removeCustomService = (i) => {
		setForm((p) =>
			({ ...p, custom_services: p.custom_services.filter((_, idx) => idx !== i) }));
	};

	// --- Image handlers ---
	const handleImages = (e) => {
		const files = Array.from(e.target.files);
		const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
		setImages(prev => [...prev, ...newImgs]);
	};
	const removeImage = (i) => {
		if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
		setImages(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleImage = (i) => setImages(prev => prev.map((img, idx) =>
		idx === i ? { ...img, enabled: !img.enabled } : img));

	const submit = async (e) => {
		e.preventDefault();
		if (!form.title.trim() || !form.description.trim() || !form.location_name.trim()) {
			toast.error(t('common.fill_required'));
			return;
		}

		const cost = computeCosts(images, [], pricing, form.is_featured, form.featured_paid, form.included_images,
			form.included_videos);

		const newImages = cost.enabledImages.filter(i => !i.isExisting);

		// Compute hidden lists from existing media
		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);

		const payload = {
			...form,
			price: parseFloat(form.price) || 0,
			experience_years: form.experience_years ? parseInt(form.experience_years, 10) : null,
			// Normalize service_prices: strip empty entries
			service_prices: Object.fromEntries(Object.entries(form.service_prices).filter(([, v]) => v !== '' && v !== null && v !== undefined)),
			// Normalize custom_services: drop rows with no name
			custom_services: form.custom_services
				.filter(s => (s.name || '').trim())
				.map(s => ({ key: 'custom', name: s.name.trim(), price: s.price === '' ? null : s.price, unit: s.unit || 'visit' })),
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			hidden_images: hiddenImgs,
		};

		await handleSubmit(payload, vetAPI, isClone ? null : editId, 'vet', cost,
			newImages, [], form.is_featured, form.featured_paid, setSaving, t, navigate);
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="create-vet-page">
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

				<BackLink title={form.title} uri="/vets" textKey='nav.back_to_results' />

				<div className="flex items-center gap-3 mb-2">
					<Stethoscope className="w-6 h-6 text-[#8B4513]" />
					<h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{ isClone ? t('vet.create_title') : editId ? t('vet.edit_title') :
							t('vet.create_title')}
					</h1>
				</div>
				<p className="text-sm text-[#666055] mb-6">{t('vet.create_subtitle')}</p>

				<form onSubmit={submit} className="space-y-6"	data-testid="vet-form">
					<Section title={t('horse.basic_info')}>
						<div className="mb-4">
							<Label>{t('horse.title_label')} *</Label>
							<Input value={form.title} onChange={(e) => set('title', e.target.value)}
										 required maxLength={120} className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
										 data-testid="vet-title" />
						</div>

						<div className="mb-4">
							<Label>{t('horse.description')} *</Label>
							<Textarea value={form.description} onChange={(e) => set('description', e.target.value)}
												required rows={5} maxLength={4000}
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1"
												data-testid="vet-description" />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
							<div>
								<Label>{t('vet.base_consultation_fee')}</Label>
								<Input type="number" min={0} step="any" value={form.price}
											 onChange={(e) => set('price', e.target.value)}
											 placeholder="€" className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
											 data-testid="vet-price" />
							</div>
							<div>
								<Label>{t('vet.experience_years')}</Label>
								<Input type="number" min={0} max={70} value={form.experience_years}
											 onChange={(e) => set('experience_years', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
											 data-testid="vet-experience-years" />
							</div>
						</div>

						{/* Emergency + mobile flags */}
						<div className="pt-2 border-t border-[#E6E2D6] space-y-2">
							<label className="flex items-start gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none mb-4">
								<Checkbox checked={form.emergency_24_7} onCheckedChange={(v) => set('emergency_24_7', !!v)}
													data-testid="vet-emergency-24-7" className="mt-0.5" />
								<div>
									<span className="font-semibold inline-flex items-center gap-1.5 text-[#9B2C2C]">
										<AlertCircle className="w-4 h-4" />{t('vet.emergency_24_7_label')}
									</span>
									<p className="text-xs text-[#666055]">{t('vet.emergency_24_7_hint')}</p>
								</div>
							</label>
							<label className="flex items-start gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none mb-4">
								<Checkbox checked={form.mobile_only} onCheckedChange={(v) => set('mobile_only', !!v)}
													data-testid="vet-mobile-only" className="mt-0.5" />
								<div>
									<span className="font-semibold">{t('vet.mobile_only_label')}</span>
									<p className="text-xs text-[#666055]">{t('vet.mobile_only_hint')}</p>
								</div>
							</label>
							<label className="flex items-start gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
								<Checkbox checked={form.travels_to_client} onCheckedChange={(v) => set('travels_to_client', !!v)}
													data-testid="vet-travels" className="mt-0.5" />
								<div>
									<span className="font-semibold">{t('vet.travels_to_client_label')}</span>
									<p className="text-xs text-[#666055]">{t('vet.travels_to_client_hint')}</p>
								</div>
							</label>
						</div>
					</Section>

					<Section title={t('vet.specialties')}>
						{/* Specialties */}
						<div className="pt-2 border-t border-[#E6E2D6] mb-4">
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
								{SPECIALTIES.map(s => (
									<label key={s} className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
										<Checkbox checked={form.specialties.includes(s)} onCheckedChange={() =>
											toggleSpecialty(s)} data-testid={`vet-specialty-${s}`} />
										{t(`vet.specialty.${s}`)}
									</label>
								))}
							</div>
						</div>
					</Section>

					<Section title={t('vet.common_services')}>
						{/* Predefined services with per-service price */}
						<div className="pt-2 border-t border-[#E6E2D6] mb-4">
							<p className="text-xs text-[#666055] mb-3">{t('vet.services_help')}</p>
							<div className="space-y-2">
								{PREDEFINED_SERVICES.map(k => {
									const checked = form.predefined_services.includes(k);
									return (
										<div key={k} className="grid grid-cols-[24px_1fr_130px] gap-3 items-center py-1.5 px-2 rounded-sm
												hover:bg-[#F9F7F2] transition-colors">
											<Checkbox checked={checked} onCheckedChange={() => togglePredefinedService(k)}
																data-testid={`vet-service-${k}`} />
											<span className="text-sm text-[#3a3a3a]">{t(`vet.service.${k}`)}</span>
											<Input
												type="number"
												min={0}
												step="any"
												placeholder={checked ? '€ / visit' : '—'}
												value={form.service_prices[k] ?? ''}
												onChange={(e) => setServicePrice(k, e.target.value)}
												disabled={!checked}
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-9 text-sm disabled:opacity-40"
												data-testid={`vet-service-price-${k}`}
											/>
										</div>
									);
								})}
							</div>
						</div>
					</Section>

					<Section title={t('vet.custom_services')}>
						{/* Custom services */}
						<div className="pt-2 border-t border-[#E6E2D6] mb-4">
							<div className="flex items-center justify-between mb-2">
								<Button type="button" size="sm" variant="outline" onClick={addCustomService}
												className="border-[#E6E2D6] rounded-sm h-8 text-xs" data-testid="vet-add-custom-service">
									<Plus className="w-3 h-3 mr-1" /> {t('vet.add_service')}
								</Button>
							</div>
							<p className="text-xs text-[#666055] mb-3">{t('vet.custom_services_hint')}</p>
							{form.custom_services.length === 0 ? (
								<p className="text-xs text-[#666055]/60 italic">{t('vet.no_custom_services')}</p>
							) : (
								<div className="space-y-2">
									{form.custom_services.map((s, i) => (
										<div key={i} className="grid grid-cols-[1fr_110px_100px_36px] gap-2 items-center"
												 data-testid={`vet-custom-service-row-${i}`}>
											<Input placeholder={t('vet.service_name_placeholder')} value={s.name}
														 onChange={(e) => updateCustomService(i, 'name', e.target.value)}
														 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 text-sm"
														 data-testid={`vet-custom-name-${i}`} />
											<Input type="number" min={0} step="any" placeholder="€" value={s.price}
														 onChange={(e) => updateCustomService(i, 'price', e.target.value)}
														 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 text-sm"
														 data-testid={`vet-custom-price-${i}`} />
											<select value={s.unit}
															onChange={(e) => updateCustomService(i, 'unit', e.target.value)}
															className="bg-[#F9F7F2] border border-[#E6E2D6] rounded-sm h-10 text-xs px-2"
															data-testid={`vet-custom-unit-${i}`}>
												{UNITS.map(u => <option key={u} value={u}>{t(`vet.unit.${u}`)}</option>)}
											</select>
											<button type="button" onClick={() => removeCustomService(i)}
															className="text-[#9B2C2C] hover:bg-[#FBEAEA] rounded-sm h-10 w-9 flex items-center
																	justify-center" data-testid={`vet-custom-remove-${i}`}>
												<Trash2 className="w-4 h-4" />
											</button>
										</div>
									))}
								</div>
							)}
						</div>
					</Section>

					<Section title={t("horse.details")} >
						{/* Credentials */}
						<div className="pt-2 border-t border-[#E6E2D6] grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label>{t('vet.license_number')}</Label>
								<Input value={form.license_number} onChange={(e) => set('license_number', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="vet-license" />
							</div>
							<div>
								<Label>{t('vet.clinic_name')}</Label>
								<Input value={form.clinic_name} onChange={(e) => set('clinic_name', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="vet-clinic-name" />
							</div>
							<div>
								<Label>{t('school.website')}</Label>
								<Input value={form.website} onChange={(e) => set('website', e.target.value)}
											 placeholder="https://..." className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
							<div>
								<Label>{t('horse.contact_phone')}</Label>
								<Input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
						</div>
					</Section>

					{/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ set }/>

					{/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

					{/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ set } />

					{/* Cost Summary */}
					<CostSummary
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						t={ t }/>

					{/* only display if there is a cost */ }
					<WaiverCheckBox
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ saving }
						label='vet.publish_btn'
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						waivedWithdrawalRight={ waivedWithdrawalRight }
					/>

				</form>
			</div>
		</div>
	);
}